import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'personal-inbound-reason-create',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzSwitchModule,
    NzDatePickerModule,
    NzGridModule,
    NzRadioModule,
    NzIconModule,
    NzToolTipModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-inbound-reason-create.component.html',
  styleUrls: ['./personal-inbound-reason-create.component.less']
})
export class PersonalInboundReasonCreateComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly commonService = inject(CommonService);

  tenantId = '';
  serviceNumberId = '';
  reasonId: string | null = null;
  returnUrl = '';
  saving = false;

  form = this.fb.group({
    name: [null, [Validators.required, Validators.maxLength(50)]],
    type: [null, [Validators.required]],
    description: [null, [Validators.maxLength(100)]],
    businessId: [null],
    validityType: ['permanent'],
    expireTime: [null],
    status: [true],
    labelIds: [[] as string[]],
    regexEnabled: [false],
    regexRules: this.fb.array([])
  });
  labelOptions: Array<{ id: string; name: string }> = [];
  labelLoading = false;

  get regexRulesArray(): FormArray {
    return this.form.get('regexRules') as FormArray;
  }

  addRegexRule(initialValue: string = ''): void {
    if (this.regexRulesArray.length >= 20) {
      this.message.warning(this.i18n.fanyi('servicenumber.inbound-reason.form.regex-rules-max'));
      return;
    }
    this.regexRulesArray.push(this.fb.control(initialValue, [Validators.maxLength(500), this.regexSyntaxValidator]));
    this.cdr.markForCheck();
  }

  removeRegexRule(index: number): void {
    this.regexRulesArray.removeAt(index);
    this.cdr.markForCheck();
  }

  onRegexEnabledChange(enabled: boolean): void {
    if (this.form.get('regexEnabled')?.value !== enabled) {
      this.form.get('regexEnabled')?.setValue(enabled);
    }
    if (enabled && this.regexRulesArray.length === 0) {
      this.addRegexRule();
    } else if (!enabled) {
      this.regexRulesArray.clear();
    }
    this.cdr.markForCheck();
  }

  regexSyntaxValidator = (control: AbstractControl) => {
    const val = control.value;
    if (!val || typeof val !== 'string' || !val.trim()) {
      return null;
    }
    try {
      new RegExp(val.trim());
      return null;
    } catch {
      return { invalidRegex: true };
    }
  };

  typeOptions = [
    { labelI18nKey: 'servicenumber.inbound-reason.type.scan-qr-code', value: 'SCAN_QR_CODE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.product-consult', value: 'PRODUCT_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.promotion-consult', value: 'PROMOTION_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.signature', value: 'SIGNATURE' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.customer-support', value: 'CUSTOMER_SUPPORT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.pricing-consult', value: 'PRICING_CONSULT' },
    { labelI18nKey: 'servicenumber.inbound-reason.type.custom', value: 'CUSTOM' }
  ];

  ngOnInit(): void {
    this.tenantId = this.route.snapshot.paramMap.get('id') || '';
    this.serviceNumberId = this.route.snapshot.queryParamMap.get('serviceNumberId') || this.commonService.getCurrentTenantBossSnid() || '';
    this.reasonId = this.route.snapshot.queryParamMap.get('reasonId');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '';
    this.loadLabelOptions();

    const expireControl = this.form.get('expireTime');
    this.form.get('validityType')?.valueChanges.subscribe(type => {
      if (type === 'custom') {
        expireControl?.setValidators([Validators.required, this.expireTimeFutureValidator]);
      } else {
        expireControl?.clearValidators();
        expireControl?.setValue(null);
      }
      expireControl?.updateValueAndValidity();
    });

    if (this.reasonId) {
      this.loadDetail(this.reasonId);
    }
  }

  cancel(): void {
    this.router.navigateByUrl(this.returnUrl || `/personal-inbound-reason/${this.tenantId}`);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.create.validation'));
      return;
    }
    if (!this.serviceNumberId) {
      this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.form.service-number-required'));
      return;
    }
    const value = this.form.getRawValue();
    const regexEnabled = !!value.regexEnabled;
    const rawRules = (this.regexRulesArray.value || []) as string[];
    const cleanedRules = rawRules.map(r => (r || '').trim()).filter(r => !!r);

    if (regexEnabled) {
      if (cleanedRules.length === 0) {
        this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.form.regex-rules-required'));
        return;
      }
      for (const rule of cleanedRules) {
        try {
          new RegExp(rule);
        } catch {
          this.message.error(this.i18n.fanyi('servicenumber.inbound-reason.form.regex-rules-invalid'));
          return;
        }
      }
    }

    const validityPeriod = value.validityType === 'custom' && value.expireTime ? new Date(value.expireTime).getTime() : 0;
    const payload = {
      id: this.reasonId || undefined,
      serviceNumberId: this.serviceNumberId,
      sourceName: value.name,
      sourceType: value.type,
      description: value.description,
      businessId: value.businessId,
      validityPeriod,
      status: value.status,
      labelIds: value.labelIds || [],
      regexEnabled,
      regexRules: regexEnabled ? cleanedRules : []
    };
    this.saving = true;
    this.http
      .post(ConstantUtil.TENANT_INBOUND_REASON_V1_SAVE, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.message.success(this.i18n.fanyi(this.reasonId ? 'common.save-success' : 'servicenumber.inbound-reason.create.success'));
          if (this.returnUrl) {
            this.router.navigateByUrl(this.returnUrl);
            return;
          }
          this.router.navigate(['/personal-inbound-reason', this.tenantId], {
            queryParams: {
              refresh: Date.now()
            }
          });
        } else {
          showFallbackHttpError(
            this.message,
            res?.msg,
            this.i18n.fanyi(this.reasonId ? 'common.save-failed' : 'servicenumber.inbound-reason.create.failed')
          );
        }
      });
  }

  private loadDetail(id: string): void {
    this.saving = true;
    this.http
      .post(`${ConstantUtil.TENANT_INBOUND_REASON_V1_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (!res?.success || !res?.data) {
          return;
        }
        const data = res.data;
        const validityType = data.validityPeriod && data.validityPeriod > 0 ? 'custom' : 'permanent';
        const regexEnabled = !!data.regexEnabled;
        this.regexRulesArray.clear();
        if (Array.isArray(data.regexRules)) {
          for (const rule of data.regexRules) {
            if (rule) {
              this.addRegexRule(rule);
            }
          }
        }
        this.form.patchValue({
          name: data.sourceName ?? data.title ?? '',
          type: data.sourceType ?? data.type ?? null,
          description: data.description ?? '',
          businessId: data.businessId ?? null,
          validityType,
          expireTime: validityType === 'custom' && data.validityPeriod ? (new Date(data.validityPeriod) as any) : null,
          status: typeof data.status === 'boolean' ? data.status : data.status === 'Enable',
          labelIds: Array.isArray(data.labelIds) ? data.labelIds : [],
          regexEnabled
        });
        this.cdr.markForCheck();
      });
  }

  /**
   * 查詢目前服務號可用的客戶標籤。
   */
  private loadLabelOptions(): void {
    if (!this.serviceNumberId) {
      this.labelOptions = [];
      return;
    }
    this.labelLoading = true;
    this.http
      .post(ConstantUtil.TENANT_LABEL_V1_LIST, {
        serviceNumberId: this.serviceNumberId,
        type: 'ServiceNumber',
        pageIndex: 0,
        pageSize: 200,
        direction: 'desc',
        refreshTime: Date.now()
      })
      .pipe(
        finalize(() => {
          this.labelLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (!res?.success) {
          this.labelOptions = [];
          return;
        }
        const items = res?.data?.items || [];
        this.labelOptions = items
          .map((item: any) => ({ id: String(item?.id || '').trim(), name: String(item?.name || '').trim() }))
          .filter((item: { id: string; name: string }) => item.id && item.name);
      });
  }

  expireTimeFutureValidator = (control: AbstractControl) => {
    const val = control.value;
    if (!val) return null;
    const selected = new Date(val);
    const today = new Date();
    selected.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selected.getTime() > today.getTime() ? null : { notFuture: true };
  };

  disablePastDates = (current: Date): boolean => {
    if (!current) return false;
    const d = new Date(current);
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() <= today.getTime();
  };
}
